/**
 * Cache debug body — renders the summary header + per-step rows.
 *
 * Shared between the full-screen {@link CacheDebugOverlay} and the Cache tab
 * of the RunDebugDrawer.
 */
import React from 'react';
import { Text, Flex } from '@chatsdet/ui';
import { useCacheDebugData } from './useCacheDebugData';
import { CacheDebugStepRow } from './CacheDebugStepRow';

type Props = {
  workflowId: string;
  runId: string;
  /** Controls whether the data hook fetches. Pass false to skip until visible. */
  enabled?: boolean;
};

export function CacheDebugContent({ workflowId, runId, enabled = true }: Props) {
  const { data, isLoading } = useCacheDebugData(workflowId, runId, enabled);
  const steps = data?.steps ?? [];

  const counts = React.useMemo(() => {
    const result = { cached: 0, recovery: 0, gap: 0, agent: 0, skipped: 0 };
    for (const step of steps) {
      if (step.cacheOutcome === 'hit') {
        if (step.cacheHitSource === 'recovery') result.recovery++;
        else result.cached++;
      } else if (step.cacheOutcome === 'fallback' || step.cacheOutcome === 'miss') {
        result.agent++;
      } else if (step.cacheOutcome === 'skipped') {
        result.skipped++;
      }
    }
    for (const step of steps) {
      if (step.stepOutcomes) {
        for (const outcome of step.stepOutcomes) {
          if (outcome.type === 'gap' && outcome.success) result.gap++;
        }
      }
    }
    return result;
  }, [steps]);

  return (
    <Flex direction="column" gap="md" className="h-full min-h-0">
      <div className="shrink-0">
        <Flex gap="sm" align="center" className="text-sm text-muted-foreground">
          {isLoading ? (
            <span>Loading…</span>
          ) : (
            <>
              <span>
                {steps.length} step{steps.length !== 1 ? 's' : ''}
              </span>
              {counts.cached > 0 && (
                <span className="text-blue-600 dark:text-blue-400">
                  &middot; {counts.cached} cached
                </span>
              )}
              {counts.recovery > 0 && (
                <span className="text-emerald-600 dark:text-emerald-400">
                  &middot; {counts.recovery} L2 recovery
                </span>
              )}
              {counts.gap > 0 && (
                <span className="text-emerald-600 dark:text-emerald-400">
                  &middot; {counts.gap} L2 gap
                </span>
              )}
              {counts.agent > 0 && (
                <span className="text-amber-600 dark:text-amber-400">
                  &middot; {counts.agent} agent
                </span>
              )}
              {counts.skipped > 0 && (
                <span className="text-gray-400">&middot; {counts.skipped} skipped</span>
              )}
            </>
          )}
        </Flex>
      </div>

      <div className="flex-1 min-h-0 rounded-lg border border-border bg-card overflow-y-auto p-4 space-y-2">
        {isLoading ? (
          <Text size="sm" className="text-muted-foreground py-8 text-center">
            Loading cache debug data…
          </Text>
        ) : steps.length === 0 ? (
          <Text size="sm" className="text-muted-foreground py-8 text-center">
            No cache data available for this run.
          </Text>
        ) : (
          steps.map((step) => <CacheDebugStepRow key={step.stepId} step={step} />)
        )}
      </div>
    </Flex>
  );
}
